import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { AuthGuard, CourseAccessGuard } from 'src/auth/guard';
import { LoadingScreen } from 'src/components/loading-screen';
import { NavigateWithParams } from 'src/components/navigate-with-params';
import { DashboardLayout } from 'src/layouts/dashboard';
import { getRelativePath, PATHS } from 'src/routes/paths';
import { RoleBasedRoute } from 'src/auth/guard/role-based-guard';
import { ChatRealtimeProvider } from 'src/services/chat-socket';
import { lazyPage, registerRoutePreloads } from 'src/routes/lazy-page';
import { DEVELOPMENT } from 'src/config-global';

const DashboardPage = lazyPage(() => import('src/pages/dashboard/dashboard'));
const CoursesPage = lazyPage(() => import('src/pages/dashboard/courses'), ['dashboard/courses']);
const SingleCoursePage = lazyPage(
  () => import('src/pages/dashboard/single-course'),
  ['dashboard/courses', 'dashboard/courses/single-course']
);
const LecturePage = lazyPage(() => import('src/pages/dashboard/lecture'), ['dashboard/lecture']);
const FeedbacksPage = lazyPage(
  () => import('src/pages/dashboard/feedbacks'),
  ['dashboard/feedbacks', 'dashboard/analytics']
);
// const CategoriesPage = lazyPage(() => import('src/pages/dashboard/categories'), ['dashboard/categories']);
const SubmissionsPage = lazyPage(
  () => import('src/pages/dashboard/submissions'),
  ['dashboard/submissions', 'dashboard/analytics']
);
const ProfileSettingsPage = lazyPage(
  () => import('src/pages/dashboard/profile-settings'),
  ['dashboard/profile-settings', 'dashboard/mcp']
);
const PlatformSettingsPage = lazyPage(
  () => import('src/pages/dashboard/platform-settings'),
  ['dashboard/platform-settings']
);
const PlatformBillingPage = lazyPage(
  () => import('src/pages/dashboard/platform-billing'),
  ['dashboard/platform-billing']
);
const StudentsPage = lazyPage(
  () => import('src/pages/dashboard/students'),
  ['dashboard/students', 'dashboard/employees']
);
const StudentsImportPage = lazyPage(
  () => import('src/pages/dashboard/students-import'),
  ['dashboard/employees']
);
const OrgStructurePage = lazyPage(
  () => import('src/pages/dashboard/org-structure'),
  ['dashboard/org-structure', 'dashboard/assignments']
);
const AssignmentsPage = lazyPage(
  () => import('src/pages/dashboard/assignments'),
  ['dashboard/assignments']
);
const SingleAssignmentPage = lazyPage(
  () => import('src/pages/dashboard/single-assignment'),
  ['dashboard/assignments']
);
const SingleStudentPage = lazyPage(
  () => import('src/pages/dashboard/single-student'),
  ['dashboard/students', 'dashboard/employees', 'dashboard/assignments']
);
const AnalyticsProgressPage = lazyPage(
  () => import('src/pages/dashboard/analytics-progress'),
  ['dashboard/analytics']
);
const AnalyticsProgressByMembersPage = lazyPage(
  () => import('src/pages/dashboard/analytics-progress-by-members'),
  ['dashboard/analytics']
);
const AnalyticsProgressByContentPage = lazyPage(
  () => import('src/pages/dashboard/analytics-progress-by-content'),
  ['dashboard/analytics']
);
const AnalyticsPointsPage = lazyPage(
  () => import('src/pages/dashboard/analytics-points'),
  ['dashboard/analytics']
);
const AnalyticsTrainingControlPage = lazyPage(
  () => import('src/pages/dashboard/analytics-training-control'),
  ['dashboard/analytics']
);
const ExamPage = lazyPage(() => import('src/pages/dashboard/exam'), ['dashboard/exam']);
const ExamTabsPage = lazyPage(
  () => import('src/pages/dashboard/exam-tabs'),
  ['dashboard/exam', 'dashboard/exam/exam-questions']
);
const ExamCreatePage = lazyPage(
  () => import('src/pages/dashboard/exam-create'),
  ['dashboard/exam/exam-create']
);
const ExamCreateFromFilePage = lazyPage(
  () => import('src/pages/dashboard/exam-create-from-file'),
  ['dashboard/exam/exam-create', 'dashboard/exam/exam-create/create-from-file']
);
const ExamCreateFromLecturesPage = lazyPage(
  () => import('src/pages/dashboard/exam-create-from-lectures'),
  ['dashboard/exam/exam-create', 'dashboard/exam/exam-create/create-from-lectures']
);
const SurveyPage = lazyPage(() => import('src/pages/dashboard/survey'), ['dashboard/survey']);
const SingleStreamPage = lazyPage(
  () => import('src/pages/dashboard/single-stream'),
  ['dashboard/single-stream']
);
const SingleGroupPage = lazyPage(
  () => import('src/pages/dashboard/single-group'),
  ['dashboard/single-group']
);
const MembersPage = lazyPage(() => import('src/pages/dashboard/members'), ['dashboard/members']);
const SingleMemberPage = lazyPage(
  () => import('src/pages/dashboard/single-member'),
  ['dashboard/members']
);
const ExamSubmissionsPage = lazyPage(
  () => import('src/pages/dashboard/exam-submissions'),
  ['dashboard/exam-submissions']
);
const SingleExamSubmissionPage = lazyPage(
  () => import('src/pages/dashboard/single-exam-submission'),
  ['dashboard/exam-submissions']
);
const PracticeResultsPage = lazyPage(
  () => import('src/pages/dashboard/practice-results'),
  ['dashboard/exam-submissions', 'dashboard/practice-results']
);
const SinglePracticeResultPage = lazyPage(
  () => import('src/pages/dashboard/single-practice-result'),
  ['dashboard/exam-submissions', 'dashboard/practice-results']
);
const CourseCollectionsPage = lazyPage(
  () => import('src/pages/dashboard/course-collections'),
  ['dashboard/course-collections']
);
const SingleCourseCollectionPage = lazyPage(
  () => import('src/pages/dashboard/single-course-collection'),
  ['dashboard/course-collections', 'dashboard/course-collections/single-course-collection']
);
const AuditLogsPage = lazyPage(
  () => import('src/pages/dashboard/audit-logs'),
  ['dashboard/audit-logs']
);
const PaymentMethodsPage = lazyPage(
  () => import('src/pages/dashboard/payment-methods'),
  ['dashboard/payment-settings']
);
const SalesHistoryPage = lazyPage(
  () => import('src/pages/dashboard/sales-history'),
  ['dashboard/sales-history', 'dashboard/analytics']
);
const ChatbotPage = lazyPage(() => import('src/pages/dashboard/chatbot'), ['dashboard/chatbot']);
const ChatsPage = lazyPage(() => import('src/pages/dashboard/chats'), ['dashboard/chat']);
const LiveSessionsPage = lazyPage(
  () => import('src/pages/dashboard/live-sessions'),
  ['dashboard/live-sessions']
);
const LiveSessionReportPage = lazyPage(
  () => import('src/pages/dashboard/live-session-report'),
  ['dashboard/live-sessions']
);
const LiveSessionCallPage = lazyPage(
  () => import('src/pages/dashboard/live-session-call'),
  ['dashboard/live-sessions']
);
const IntegrationsPage = lazyPage(
  () => import('src/pages/dashboard/integrations'),
  ['dashboard/integrations', 'dashboard/mcp']
);
const AiAgentsPage = lazyPage(() => import('src/pages/dashboard/ai-agents'), ['dashboard/mcp']);
const SingleIntegrationPage = lazyPage(
  () => import('src/pages/dashboard/single-integration'),
  ['dashboard/integrations']
);

export const appRoutes = [
  {
    element: (
      <AuthGuard>
        <ChatRealtimeProvider>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <RoleBasedRoute>
                <Outlet />
              </RoleBasedRoute>
            </Suspense>
          </DashboardLayout>
        </ChatRealtimeProvider>
      </AuthGuard>
    ),
    children: [
      { path: PATHS.root, element: <NavigateWithParams to={PATHS.app.courses} replace /> },
      { path: PATHS.app.dashboard, element: <DashboardPage /> },
      { path: PATHS.app.courses, element: <CoursesPage /> },
      {
        path: PATHS.app.singleCourse,
        element: (
          <CourseAccessGuard>
            <SingleCoursePage />
          </CourseAccessGuard>
        ),
      },
      {
        path: PATHS.app.lecture,
        element: (
          <CourseAccessGuard>
            <LecturePage />
          </CourseAccessGuard>
        ),
      },
      { path: PATHS.app.survey, element: <SurveyPage /> },
      // { path: PATHS.app.categories, element: <CategoriesPage /> },
      { path: PATHS.app.students, element: <StudentsPage /> },
      { path: PATHS.app.feedbacks, element: <FeedbacksPage /> },
      ...(DEVELOPMENT ? [{ path: PATHS.app.studentsImport, element: <StudentsImportPage /> }] : []),
      { path: PATHS.app.singleStudent, element: <SingleStudentPage /> },
      ...(DEVELOPMENT ? [{ path: PATHS.app.orgStructure, element: <OrgStructurePage /> }] : []),
      ...(DEVELOPMENT
        ? [
            { path: PATHS.app.assignments, element: <AssignmentsPage /> },
            { path: PATHS.app.singleAssignment, element: <SingleAssignmentPage /> },
          ]
        : []),
      { path: PATHS.app.profileSettings, element: <ProfileSettingsPage /> },
      { path: PATHS.app.platformSettings, element: <PlatformSettingsPage /> },
      { path: PATHS.app.platformBilling, element: <PlatformBillingPage /> },
      { path: PATHS.app.analyticsProgress, element: <AnalyticsProgressPage /> },
      {
        path: PATHS.app.analyticsProgressByMembers,
        element: <AnalyticsProgressByMembersPage />,
      },
      {
        path: PATHS.app.analyticsProgressByContent,
        element: <AnalyticsProgressByContentPage />,
      },
      { path: PATHS.app.analyticsPoints, element: <AnalyticsPointsPage /> },
      ...(DEVELOPMENT
        ? [
            {
              path: PATHS.app.analyticsTrainingControl,
              element: <AnalyticsTrainingControlPage />,
            },
          ]
        : []),
      { path: PATHS.app.submissions, element: <SubmissionsPage /> },
      {
        // Layout route: renders the exam header + <Outlet /> for all child views.
        path: PATHS.app.exam,
        element: (
          <CourseAccessGuard>
            <ExamPage />
          </CourseAccessGuard>
        ),
        children: [
          { index: true, element: <ExamTabsPage /> },
          {
            path: getRelativePath(PATHS.app.exam, PATHS.app.examCreate),
            element: <ExamCreatePage />,
          },
          {
            path: getRelativePath(PATHS.app.exam, PATHS.app.examCreateFromFileStep),
            element: <ExamCreateFromFilePage />,
          },
          {
            path: getRelativePath(PATHS.app.exam, PATHS.app.examCreateFromLecturesStep),
            element: <ExamCreateFromLecturesPage />,
          },
        ],
      },
      { path: PATHS.app.singleStream, element: <SingleStreamPage /> },
      { path: PATHS.app.singleGroup, element: <SingleGroupPage /> },
      { path: PATHS.app.members, element: <MembersPage /> },
      { path: PATHS.app.singleMember, element: <SingleMemberPage /> },
      { path: PATHS.app.examSubmissions, element: <ExamSubmissionsPage /> },
      { path: PATHS.app.singleExamSubmission, element: <SingleExamSubmissionPage /> },
      { path: PATHS.app.practiceResults, element: <PracticeResultsPage /> },
      { path: PATHS.app.singlePracticeResult, element: <SinglePracticeResultPage /> },
      { path: PATHS.app.courseCollections, element: <CourseCollectionsPage /> },
      { path: PATHS.app.singleCourseCollection, element: <SingleCourseCollectionPage /> },
      { path: PATHS.app.auditLogs, element: <AuditLogsPage /> },
      { path: PATHS.app.paymentMethods, element: <PaymentMethodsPage /> },
      { path: PATHS.app.salesHistory, element: <SalesHistoryPage /> },
      { path: PATHS.app.chatbot, element: <ChatbotPage /> },
      { path: PATHS.app.chats, element: <ChatsPage /> },
      { path: PATHS.app.singleChat, element: <ChatsPage /> },
      { path: PATHS.app.liveSessions, element: <LiveSessionsPage /> },
      { path: PATHS.app.liveSessionReport, element: <LiveSessionReportPage /> },
      { path: PATHS.app.integrations, element: <IntegrationsPage /> },
      { path: PATHS.app.aiAgents, element: <AiAgentsPage /> },
      { path: PATHS.app.singleIntegration, element: <SingleIntegrationPage /> },
    ],
  },
  // The call runs full screen: no dashboard chrome to distract from it, and a document of its own so the COOP/COEP headers the Zoom Meeting SDK needs apply to this route only.
  {
    element: (
      <AuthGuard>
        <Suspense fallback={<LoadingScreen />}>
          <RoleBasedRoute>
            <Outlet />
          </RoleBasedRoute>
        </Suspense>
      </AuthGuard>
    ),
    children: [{ path: PATHS.app.liveSessionCall, element: <LiveSessionCallPage /> }],
  },
];

registerRoutePreloads(appRoutes);
